import { MissionAssessmentListItemDto, type TechnologySelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { useGetCurrentUser } from '@queries/users.query';
import { ArrowRight } from 'lucide-react';
import { FC, useMemo } from 'react';
import { AccordionTile } from '../../shared/accordion-tile.component';
import {
  type CoverageRow,
  buildExpertCoverageRows,
  buildInnovatorCoverageRows,
  getRequestSortWeight,
  sortByCreatedAtDescending,
  sortByUpdatedAtDescending,
} from './ma-overview-coverage.util';
import { type RequestListItem, RequestTile } from './ma-overview-request-tile.component';

type MaOverviewContentProps = {
  canRequestAssessment?: boolean;
  onOpenRequestModal?: () => void;
  onSelectAssessment: (assessmentId: string) => void;
  technology: TechnologySelectDto;
  versions: MissionAssessmentListItemDto[];
};

const MISSION_ASSESSMENT_OVERVIEW_INFO =
  'A Mission Assessment (MA) evaluates how a technology aligns to specific defense missions. It captures requests, tracks Use and Domain coverage, and links each assessment back to the mission context it supports.';

export const MaOverviewContent: FC<MaOverviewContentProps> = (props) => {
  const { canRequestAssessment = false, onOpenRequestModal, onSelectAssessment, technology, versions } = props;
  const { data: user, isExpert } = useGetCurrentUser();
  const currentUserId = user?.id;

  const sortedVersions = useMemo(() => {
    return [...versions].sort(sortByUpdatedAtDescending);
  }, [versions]);

  const assessmentByRequestId = useMemo(() => {
    const requestMap = new Map<string, MissionAssessmentListItemDto>();

    for (const version of sortedVersions) {
      const requestId = version.request?.id;

      if (!requestId || requestMap.has(requestId)) {
        continue;
      }

      requestMap.set(requestId, version);
    }

    return requestMap;
  }, [sortedVersions]);

  const requestsByCreatedAt = useMemo(() => {
    return [...(technology.missionAssessmentRequests ?? [])].sort(sortByCreatedAtDescending);
  }, [technology.missionAssessmentRequests]);

  const requests = useMemo(() => {
    return [...requestsByCreatedAt].sort((left, right) => {
      const weightDifference = getRequestSortWeight(left.status) - getRequestSortWeight(right.status);

      if (weightDifference !== 0) {
        return weightDifference;
      }

      return sortByCreatedAtDescending(left, right);
    });
  }, [requestsByCreatedAt]);

  const requestItems = useMemo<RequestListItem[]>(() => {
    return requests.map((request) => ({
      assessment: assessmentByRequestId.get(request.id),
      request,
    }));
  }, [assessmentByRequestId, requests]);

  const coverageRows = useMemo<CoverageRow[]>(() => {
    if (isExpert) {
      return buildExpertCoverageRows({ sortedVersions });
    }

    return buildInnovatorCoverageRows({
      currentUserId,
      isExpert,
      requestsByCreatedAt,
      sortedVersions,
      technologyUses: (technology.uses ?? []).map((use) => ({
        domains: use.domains.map((domain) => ({
          id: domain.id,
          name: domain.name,
          position: domain.position,
        })),
        name: use.name,
        position: use.position,
        useId: use.useId,
      })),
    });
  }, [currentUserId, isExpert, requestsByCreatedAt, sortedVersions, technology.uses]);

  return (
    <div className="flex flex-col gap-4">
      <AccordionTile
        content={MISSION_ASSESSMENT_OVERVIEW_INFO}
        title="Mission Assessment"
        value="ma-overview-description"
      />

      {!isExpert && (
        <Tile
          emptyState={{
            message: canRequestAssessment && onOpenRequestModal ? 'Request Assessment' : undefined,
            onClick: canRequestAssessment && onOpenRequestModal ? onOpenRequestModal : undefined,
            title: 'No Mission Assessment Requests',
          }}
          header={{
            actions:
              canRequestAssessment && onOpenRequestModal && requestItems.length > 0 ? (
                <Button
                  onClick={onOpenRequestModal}
                  variant="link"
                >
                  + Request Assessment
                </Button>
              ) : undefined,
            title: 'Requests',
          }}
          isEmpty={requestItems.length === 0}
        >
          <div className="flex flex-col gap-3">
            {requestItems.map((item) => (
              <RequestTile
                canRequestAssessment={canRequestAssessment}
                isAssignedToYou={false}
                item={item}
                key={item.request.id}
                onSelectAssessment={onSelectAssessment}
                technologyId={technology.id}
              />
            ))}
          </div>
        </Tile>
      )}

      <Tile
        description={
          isExpert
            ? 'Your Mission Assessments for this technology, grouped by Use and Domain.'
            : 'Use and Domain coverage for this technology, including configured combinations and any requests or assessments already created.'
        }
        header={{ title: 'Coverage' }}
      >
        {coverageRows.length > 0 ? (
          <CoverageTable
            coverageRows={coverageRows}
            isExpert={isExpert}
            onSelectAssessment={onSelectAssessment}
          />
        ) : (
          <p className="text-sm text-gray-500">
            {isExpert ? 'No Mission Assessments assigned to you yet.' : 'No Use and Domain coverage activity yet.'}
          </p>
        )}
      </Tile>
    </div>
  );
};

type CoverageTableProps = {
  coverageRows: CoverageRow[];
  isExpert: boolean;
  onSelectAssessment: (assessmentId: string) => void;
};

const CoverageTable: FC<CoverageTableProps> = (props) => {
  const { coverageRows, isExpert, onSelectAssessment } = props;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/80">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Use</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Domain</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {coverageRows.map((row) => (
              <CoverageTableRow
                isExpert={isExpert}
                key={`${row.useId}-${row.domainId}`}
                onSelectAssessment={onSelectAssessment}
                row={row}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

type CoverageTableRowProps = {
  isExpert: boolean;
  onSelectAssessment: (assessmentId: string) => void;
  row: CoverageRow;
};

const getCoverageBadgeVariant = (status: CoverageRow['status']): 'outline' | 'pending' | 'primary' | 'success' => {
  switch (status) {
    case 'Assessed':
      return 'success';
    case 'In Progress':
      return 'pending';
    case 'Requested':
      return 'primary';
    default:
      return 'outline';
  }
};

const CoverageTableRow: FC<CoverageTableRowProps> = (props) => {
  const { isExpert, onSelectAssessment, row } = props;
  const assessment = row.assessment;

  return (
    <tr className="group border-b border-gray-200 last:border-b-0">
      <td className="px-4 py-4 align-top">
        <div className="text-sm font-semibold text-gray-900">{row.useName}</div>
      </td>
      <td className="px-4 py-4 align-top">
        <div className="text-sm text-gray-700">{row.domainName}</div>
      </td>
      <td className="px-4 py-4 align-top">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Badge
              size="xs"
              variant={getCoverageBadgeVariant(row.status)}
            >
              {row.status}
            </Badge>

            {!isExpert && row.isAssignedToYou && (
              <Badge
                size="xs"
                variant="expert"
              >
                Assigned to you
              </Badge>
            )}
          </div>

          {assessment && !assessment.accessRestricted && (
            <IconButton
              ariaLabel={row.isAssignedToYou ? 'Open your assessment' : 'Open assessment'}
              className="invisible opacity-0 transition-opacity group-hover:visible group-hover:opacity-100"
              icon={<ArrowRight />}
              onClick={() => onSelectAssessment(assessment.id)}
              size="sm"
              title={row.isAssignedToYou ? 'Open your assessment' : 'Open assessment'}
              variant="secondary"
            />
          )}
        </div>
      </td>
    </tr>
  );
};
